import { useCallback } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Select,
  Group,
  Button,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { SurgeryCenterLab } from "../../../services/admin/surgeryCentersLabs.service";
import { usePathologists } from "../../../hooks/useAdmin";
import {
  scrollToFirstError,
  required,
  phone,
  fax,
} from "../../../utils/formValidation";
import { SurgeryCenterLabLogoUpload } from "./SurgeryCenterLabLogoUpload";
import { ReportFooterEditor } from "../shared/ReportFooterEditor";

export interface SurgeryCenterLabFormValues {
  name: string;
  type: "surgery_center" | "lab";
  address: string;
  primaryPhone: string;
  secondaryPhone: string;
  fax: string;
  medicalDirector: string;
  footerHtml: string;
  externalCaseNumberPrefix: string;
  preferredPathologistId: string;
  logoS3Key: string;
}

interface SurgeryCenterLabFormProps {
  initialValues?: SurgeryCenterLab;
  onSubmit: (values: SurgeryCenterLabFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  /** Default type when creating new (defaults to "surgery_center") */
  defaultType?: "surgery_center" | "lab";
  /** Lock the type when a parent workflow creates only one organization kind. */
  fixedType?: "surgery_center" | "lab";
  /** When set, the <form> element gets this id so an external footer button can submit it via form={formId}. */
  formId?: string;
  /** Hide the form's built-in footer (used when the modal provides its own footer). */
  hideFooter?: boolean;
}

export default function SurgeryCenterLabForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
  defaultType = "surgery_center",
  fixedType,
  formId,
  hideFooter = false,
}: SurgeryCenterLabFormProps) {
  const { data: pathologists = [] } = usePathologists();

  const form = useForm<SurgeryCenterLabFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      type: fixedType || initialValues?.type || defaultType,
      address: initialValues?.address || "",
      primaryPhone: initialValues?.primaryPhone || "",
      secondaryPhone: initialValues?.secondaryPhone || "",
      fax: initialValues?.fax || "",
      medicalDirector: initialValues?.medicalDirector || "",
      footerHtml: initialValues?.footerHtml || "",
      externalCaseNumberPrefix: initialValues?.externalCaseNumberPrefix || "",
      preferredPathologistId: initialValues?.preferredPathologistId || "",
      logoS3Key: initialValues?.logoS3Key || "",
    },
    validate: {
      name: required("Name is required"),
      primaryPhone: phone(),
      secondaryPhone: phone(),
      fax: fax(),
    },
    validateInputOnBlur: true,
  });

  const handleSubmit = form.onSubmit(
    (values) => onSubmit(values),
    () => scrollToFirstError(form)
  );
  const handleFooterHtmlChange = useCallback(
    (value: string) => form.setFieldValue("footerHtml", value),
    [form]
  );

  return (
    <form noValidate onSubmit={handleSubmit} id={formId}>
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Enter name"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Select
          label="Type"
          withAsterisk
          allowDeselect={false}
          disabled={Boolean(fixedType)}
          data={[
            { value: "surgery_center", label: "Surgery Center" },
            { value: "lab", label: "Lab" },
          ]}
          description={
            form.values.type === "surgery_center"
              ? "Facility where procedures are performed"
              : "External laboratory that sends specimens"
          }
          {...form.getInputProps("type")}
        />

        <SurgeryCenterLabLogoUpload
          currentLogoS3Key={form.values.logoS3Key}
          onLogoChange={(key) => form.setFieldValue("logoS3Key", key ?? "")}
        />

        <Textarea
          label="Address"
          placeholder="Enter full address"
          minRows={2}
          {...form.getInputProps("address")}
        />

        <Group grow>
          <TextInput
            label="Primary Phone"
            placeholder="Enter primary phone"
            {...form.getInputProps("primaryPhone")}
          />
          <TextInput
            label="Secondary Phone"
            placeholder="Enter secondary phone"
            {...form.getInputProps("secondaryPhone")}
          />
        </Group>

        <TextInput
          label="Fax"
          placeholder="Enter fax number"
          {...form.getInputProps("fax")}
        />

        <TextInput
          label="Medical Director"
          placeholder="Enter the report-facing name and credentials (optional)"
          description="Displayed in report headers when this location is the configured identity source."
          {...form.getInputProps("medicalDirector")}
        />

        <ReportFooterEditor
          value={form.values.footerHtml}
          onChange={handleFooterHtmlChange}
        />

        <TextInput
          label="External Case Number Prefix"
          placeholder="e.g., BAE-"
          description="Prefix for cases from this location (used in accessioning)"
          {...form.getInputProps("externalCaseNumberPrefix")}
        />

        <Select
          label="Preferred Pathologist"
          placeholder="Select preferred pathologist"
          data={pathologists
            .filter((p) => p.fullName)
            .map((p) => ({
              value: p.id,
              label: p.fullName as string,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))}
          searchable
          clearable
          {...form.getInputProps("preferredPathologistId")}
        />

        {!hideFooter && (onCancel || submitLabel !== undefined) && (
          <Group
            justify={onCancel ? "space-between" : "flex-end"}
            gap="xs"
            mt="md"
          >
            {onCancel && (
              <Button variant="default" onClick={onCancel} type="button">
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isSubmitting}>
              {submitLabel || (initialValues ? "Update" : "Create")}
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
